import { useEffect, useId, useMemo, useRef, useState } from 'react';

export default function ImageDrop({
  title,
  hint,
  uploadLabel = 'Upload',
  clearLabel = 'Clear',
  accept = 'image/*',
  valueDataUrl,
  valueName,
  onFile,
  onClear,
  disabled,
}) {
  const id = useId();
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const meta = useMemo(() => {
    const name = String(valueName || '').trim();
    return name ? name : '';
  }, [valueName]);

  useEffect(() => {
    function onWindowDragOver(e) {
      if (!e.dataTransfer) return;
      if (!e.dataTransfer.types?.includes?.('Files')) return;
      e.preventDefault();
    }
    window.addEventListener('dragover', onWindowDragOver);
    return () => window.removeEventListener('dragover', onWindowDragOver);
  }, []);

  function pickFile() {
    if (disabled) return;
    inputRef.current?.click?.();
  }

  function handleFiles(list) {
    const file = list?.[0] || null;
    if (!file) return;
    onFile?.(file);
  }

  return (
    <div className={`ImageDrop ${disabled ? 'ImageDrop--disabled' : ''}`}>
      <input
        ref={inputRef}
        id={id}
        className="ImageDropInput"
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={(e) => handleFiles(e.target.files)}
      />

      <button
        type="button"
        className={`ImageDropZone ${isDragging ? 'ImageDropZone--dragging' : ''}`}
        onClick={pickFile}
        disabled={disabled}
        onDragEnter={(e) => {
          if (disabled) return;
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          setIsDragging(false);
          handleFiles(e.dataTransfer?.files);
        }}
        aria-describedby={`${id}-hint`}
      >
        {valueDataUrl ? (
          <div className="ImageDropPreview">
            <img className="ImageDropPreviewImg" src={valueDataUrl} alt="" />
          </div>
        ) : (
          <div className="ImageDropEmpty" aria-hidden="true">
            <div className="ImageDropIcon">⬆</div>
            <div>
              <div className="ImageDropEmptyTitleRow">
                <div className="ImageDropEmptyTitle">{title}</div>
                {hint ? (
                  <span className="InfoTip" title={hint} aria-label={hint}>
                    ⓘ
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </button>

      <div className="ImageDropRow" id={`${id}-hint`}>
        <div className="ImageDropMeta">
          {meta ? <span className="ImageDropMetaName">{meta}</span> : null}
        </div>
        <div className="ImageDropActions">
          {hint ? (
            <span className="InfoTip" title={hint} aria-label={hint}>
              ⓘ
            </span>
          ) : null}
          <button type="button" className="Btn Btn--ghost" onClick={pickFile} disabled={disabled}>
            {uploadLabel}
          </button>
          {valueDataUrl && onClear ? (
            <button type="button" className="Btn Btn--ghost" onClick={onClear} disabled={disabled}>
              {clearLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
